import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material'
import { FormProps } from '@yard/app/models/form-props.model'
import { useTranslate } from '@tolgee/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { DGSegregationRuleFormProfile } from './dg-segregation-rules-table-form.profile'
import { schema } from './dg-segregation-rules-table-form.scheme'

interface Props extends FormProps {
  initialValues: DGSegregationRuleFormProfile
  selectedCellLabel?: string
  onSubmit: (formValues: DGSegregationRuleFormProfile) => Promise<void> | void
}

const DGSegregationRulesTableForm = ({
  id,
  initialValues,
  onSubmit,
  selectedCellLabel,
}: Props) => {
  const { handleSubmit, control, formState, reset } = useForm<DGSegregationRuleFormProfile>({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  })
  const isProhibited = useWatch({ control, name: 'isProhibited' })

  const { t } = useTranslate()

  useEffect(() => {
    reset(initialValues)
  }, [initialValues.distance, initialValues.isProhibited, reset])

  return (
    <Box
      id={id}
      component='form'
      noValidate
      autoComplete='off'
      onSubmit={handleSubmit(onSubmit)}
      my={1}
    >
      <Stack gap={1}>
        {selectedCellLabel && (
          <Typography variant='body2' color='text.secondary'>
            {t('dgSegregationRulesSelectedClasses', 'Selected classes')}:{' '}
            <strong>{selectedCellLabel}</strong>
          </Typography>
        )}
        <Typography>
          {t('dgSegregationRulesFormDescription', 'Set distance in feet between IMO classes or clear existing selected rules.')}
        </Typography>
        <Controller
          name='isProhibited'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} />}
              label={t('isProhibited', 'Prohibited')}
            />
          )}
        />
        <Controller
          name='distance'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              required={!isProhibited}
              disabled={isProhibited}
              type='number'
              label={t('distance', 'Distance')}
              error={!!formState.errors.distance}
              helperText={
                isProhibited
                  ? t('distanceIgnoredForProhibitedDGSegregationRule', 'Distance is set to 0 for prohibited rules')
                  : formState.errors.distance?.message
              }
            />
          )}
        />
      </Stack>
    </Box>
  )
}

export default DGSegregationRulesTableForm
